import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import styled from "styled-components";

import {
  DropdownSingle,
  PaddingTopBottom20,
  colors2,
  formatCamelCaseToSentenceCase,
  useBannerContext
} from "oolib";
import { Accordion } from "oolib/dist/v2/components/Accordion";
import { TabBarStyle2 } from "oolib/dist/v2/components/TabBar";

import { SidebarLayoutV2 } from "../../layout/SidebarLayoutV2";
import { useDynamicLayoutHeight } from "../InsightMatrix/utils/useDynamicLayoutHeight";
import FilterModule from "../../discovery/FilterModule";
import { useGetQueryData } from "../../../utils/react-query-hooks/general";
import { getAnnoMode } from "../../../utils/getAnnoMode";
import { extractAllBlocksFromTpl } from "@okf/ootils";
import { MasonryCardGridShell } from "../AnnotationExplorer/comps/MasonryCardGridShell";
import { convertTplsToOptions } from "./utils";
import { DataVizWidgetShell } from "../reports/ReportLanding/comps/WidgetRenderer/comps/DataVizWidgetShell";
import { generateWidgetActions } from "../reports/ReportLanding/utils/generateWidgetActions";
import { useAutoGenFilterConfigsFromTpl } from "../utils/useAutoGenFilterConfigsFromTpl";
import {
  getSessionStorageDataForListingPage,
  getSessionStorageKeyForListingPage,
} from "@/pageTpls/ListingPage/newUtils/sessionStorageUtils";
import { BreakdownModule } from "../DataPivot/comps/BreakdownModule";
import { getValidBreakdownFields } from "../InsightMatrix/utils/getValidFieldsForQuantViz";
import { allWidgetsBaseConfs } from "../reports/ReportLanding/CONFIG";
import { useGetDataQuery } from "@/utils/react-query-hooks/getData";
import { TotalCount } from "@/pageTpls/ListingPage/comps/TotalCount";
import { useUpdateFiltersAndSearchInSessionStorage } from "../DataPivot/utils/useUpdateFiltersAndSearchInSessionStorage";
import { handleVizSegmentClick } from "../DataPivot/utils/handleVizSegmentClick";
import { getFilterKeyForBlock } from "@okf/ootils";
import { OverviewDashboardContextProvider, useOverviewDashboardContext } from "./OverviewDashboardContext";
import { WidgetLoadingState } from "../reports/ReportLanding/comps/WidgetRenderer/comps/altStatePlaceholders";

// Lazy load heavy widget components (#5)
const WidgetBarChart = lazy(() => 
  import("../reports/ReportLanding/comps/WidgetComps/WidgetBarChart").then(m => ({ default: m.Component }))
);
const WidgetAISummary = lazy(() => 
  import("../reports/ReportLanding/comps/WidgetComps/WidgetAISummary").then(m => ({ default: m.Component }))
);
const WidgetAICategorization = lazy(() => 
  import("../reports/ReportLanding/comps/WidgetComps/WidgetAICategorization").then(m => ({ default: m.Component }))
);
const WidgetHeatMapGrid = lazy(() => 
  import("../reports/ReportLanding/comps/WidgetComps/WidgetHeatMapGrid").then(m => ({ default: m.Component }))
);
const WidgetPieChart = lazy(() => 
  import("../reports/ReportLanding/comps/WidgetComps/WidgetPieChart").then(m => ({ default: m.Component }))
);

// Styled components (#14)
const SidebarSection = styled.div`
  padding: 8px 14px;
  border-bottom: 1px solid ${colors2.grey10};
  margin-bottom: 8px;
`;

const TabBarWrapper = styled.div`
  padding: 0.6rem 1.4rem;
  border-bottom: 1px solid ${colors2.grey10};
`;

const FilterModuleWrapper = styled.div`
  height: ${props => props.$height};
`;

const FullHeightDiv = styled.div`
  height: 100%;
`;

const MainContentWrapper = styled.div`
  height: 100%;
  padding: 0px 14px;
`;

const TotalCountWrapper = styled.div`
  margin-bottom: 14px;
`;

// Component lookup for lazy-loaded widgets
const comps = {
  WidgetHeatMapGrid,
  WidgetBarChart,
  WidgetPieChart,
  WidgetAISummary,
  WidgetAICategorization,
};

const quantComps = [
  "TagsInputMulti",
  "TagsInputSingle",
  "CheckboxList",
  "RadioList",
  "DropdownSingle",
  "DropdownMulti",
  "NumberInput",
  "GroupQuestionsInputSingle",
];

import { qualComps } from "../utils/qualComps";
import { blockRegistry } from "@okf/ootils";

const getDefaultWidgetComp = (formComp) => {
  if (formComp === "GroupQuestionsInputSingle") {
    return "WidgetHeatMapGrid";
  }
  return "WidgetBarChart";
};

const getDimensionLabel = (valuePath, allBlocks) => {
  const block = allBlocks.find(b => b.valuePath === valuePath);
  if (block) {
    return block.props?.shortLabel || 
           block.props?.label || 
           formatCamelCaseToSentenceCase(valuePath.replace("main.", ""));
  }
  return formatCamelCaseToSentenceCase(valuePath.replace("main.", ""));
};

// Memoized widget renderer component (#11)
const WidgetRenderer = memo(({
  widget,
  index,
  activeFilters,
  breakdownDimension,
  onVizSegmentClick,
  loadedWidgetsTracker,
  taxonomyRole,
}) => {
  const Comp = comps[widget.widgetComp];
  const shouldEnableOnClick = true; // All comps including NumberInput now support click-to-filter

  if (index !== 0 && loadedWidgetsTracker.every(tracker => tracker.index !== (index - 1))) {
    return <WidgetLoadingState />;
  }

  // For annotations mode (WidgetAICategorization), the breakdown is passed
  // as a dimension object to _aggrMetricsAcrossDimensions against the annotations
  // collection. The { contentType, valuePath } form lets the service resolve
  // the block on the parent doc template.
  const annoBreakdownDimension = breakdownDimension
    ? { contentType: widget.contentType, valuePath: breakdownDimension.value }
    : undefined;

  return (
    <Suspense fallback={<WidgetLoadingState />}>
      <Comp
        id={widget.id}
        style={{ maxHeight: "550px" }}
        blockValuePath={widget.blockValuePath}
        label={widget.label}
        contentType={widget.contentType}
        dimensions={widget.dimensions}
        widgetIdx={index}
        activeFilters={activeFilters}
        breakdownDisplayType={widget.breakdownDisplayType}
        dimension1Label={widget?.dimension1Label}
        dimension2Label={widget?.dimension2Label}
        breakdownCategoryName={widget?.breakdownCategoryName}
        enableVisibilityFetching={true}
        {...(shouldEnableOnClick && !breakdownDimension && {
          onClick: (value) => onVizSegmentClick({ value, activeBlock: widget })
        })}
        taxonomyRole={taxonomyRole}
        breakdownDimension={annoBreakdownDimension}
      />
    </Suspense>
  );
});

WidgetRenderer.displayName = 'WidgetRenderer';

const OverviewDashboard = (props) => {
  return (
    <OverviewDashboardContextProvider>
      <OverviewDashboardInner {...props}/>
    </OverviewDashboardContextProvider>
  );
};

const OverviewDashboardInner = ({
  contentType: contentTypeProp,
  filterConfig: filterConfigProp,
  usedIn,
  presetActiveFilters = [],
}) => {
  const { loadedWidgetsTracker, setLoadedWidgetsTracker, emptyWidgetIds, setEmptyWidgetIds, setTrackerResetKey } = useOverviewDashboardContext();
  const allTpls = useGetQueryData("allTpls");
  const platformConfigs = useGetQueryData("platformConfigs");
  const hasExperimentalTopicModelling = getAnnoMode(platformConfigs) !== "legacy";
  const isSplitMode = getAnnoMode(platformConfigs) === "split";

  const { parentWrapperRef, gridHeightCSS } = useDynamicLayoutHeight({ observeLayoutChanges: true });

  // Cache sessionStorage key computation
  const sessionStorageKey = useMemo(() => 
    getSessionStorageKeyForListingPage({ contentTypes: [contentTypeProp] }),
    [contentTypeProp]
  );

  // Cache sessionStorage read (#8) - only read once on mount
  const [sessionStorageData] = useState(() => 
    getSessionStorageDataForListingPage(sessionStorageKey)
  );

  const [activeFilters, setActiveFilters] = useState(
    usedIn === "PublishedListing" ? sessionStorageData?.activeFilters || [] : []
  );

  // Merge preset filters (e.g. collection tag scoping) with user-controlled filters
  const mergedActiveFilters = useMemo(
    () => [...presetActiveFilters, ...activeFilters],
    [presetActiveFilters, activeFilters]
  );

  useUpdateFiltersAndSearchInSessionStorage({
    sessionStorageKey,
    activeFilters,
    dontUpdateCondition: usedIn !== 'PublishedListing'
  });

  const onlyPublishingTpls = useMemo(() => 
    allTpls.filter((tpl) => tpl.general?.segment === "publishing"),
    [allTpls]
  );

  const tplOptions = useMemo(
    () => convertTplsToOptions(onlyPublishingTpls),
    [onlyPublishingTpls]
  );

  const [selectedTpl, setSelectedTpl] = useState(
    contentTypeProp || tplOptions[0]?.value
  );

  const { SET_INFO_BANNER, SET_VARIANT } = useBannerContext();

  // Always call the hook, use prop as override (#7)
  const generatedFilterConfig = useAutoGenFilterConfigsFromTpl({
    selectedTpls: [onlyPublishingTpls.find((tpl) => tpl.kp_content_type === selectedTpl)],
    filterScopes: ["doc"],
  });
  const filterConfig = filterConfigProp || generatedFilterConfig;

  // Convert useEffect to useMemo for derived state (#6)
  const { allBlocks, vizData: baseVizData } = useMemo(() => {
    const tpl = onlyPublishingTpls.find(
      (t) => t.kp_content_type === selectedTpl
    );
    const blocks = extractAllBlocksFromTpl({ tpl });

    const getTitleForThisBlockDataViz = (block) => {
      const getTitleFromTagTypeName = (bl) => {
        if (bl.valuePath.startsWith("tags.")) {
          return allTpls.find(
            (t) => t.kp_content_type === bl.valuePath.replace("tags.", "")
          )?.general?.content?.title;
        }
        return undefined;
      };

      return (
        block.props?.shortLabel ||
        block.props?.label ||
        getTitleFromTagTypeName(block) ||
        block.valuePath
      );
    };

    // Document Author widget - shown first
    const documentAuthorWidget = {
      display: 'Document Author',
      comp: 'UserReference',
      widgetComp: 'WidgetBarChart',
      id: 'meta.kp_contributed_by',
      contentType: selectedTpl,
      blockValuePath: 'meta.kp_contributed_by',
      label: 'Document Author',
      dimensions: ['meta.kp_contributed_by'],
      annotationEnabled: false,
      filterKey: null,
    };

    // Combined single iteration (#13)
    const categorizedBlocks = [];
    for (const d of blocks) {
      // Registered blocks honor instance-level qualConditions (e.g. AudioInput
      // only counts as qual when transcription.enable === true). Unregistered
      // blocks fall back to the legacy comp-name list.
      const isQual = blockRegistry.isRegistered(d.comp)
        ? blockRegistry.isQualBlock(d)
        : qualComps.includes(d.comp);
      if (quantComps.includes(d.comp) || isQual) {
        // Annotation-capable via the registry (instance-aware — honours the per-block
        // kill-switch props.annotations.disable). Replaces the removed props.annotation.enable flag.
        const annotationEnabled = blockRegistry.hasCapability(d.comp, 'annotation', d);
        // When experimental new topic modelling is on and the block supports
        // annotations, default this widget to Annotations: Thematics.
        const defaultToAnnotationsThematic =
          isQual && hasExperimentalTopicModelling && annotationEnabled;
        categorizedBlocks.push({
          display:
            d.props?.shortLabel ||
            d.props?.label ||
            formatCamelCaseToSentenceCase(d.valuePath.replace("main.", "")),
          comp: d.comp,
          widgetComp: isQual
            ? defaultToAnnotationsThematic
              ? "WidgetAICategorization"
              : "WidgetAISummary"
            : getDefaultWidgetComp(d.comp),
          // Default taxonomyRole when we're forcing annotations as the initial
          // view. Users can still switch via the action menu; their choice lives
          // in widgetUIState which is merged on top of this. Split has no roles —
          // its default is the flat all-sub-themes view (no taxonomyRole),
          // matching its "Annotations" action.
          ...(defaultToAnnotationsThematic && !isSplitMode ? { taxonomyRole: "thematic" } : {}),
          breakdownDisplayType:
            d.comp === "GroupQuestionsInputSingle" ? "grouped" : undefined,
          id: d.valuePath,
          contentType: selectedTpl,
          blockValuePath: d.valuePath,
          label: getTitleForThisBlockDataViz(d),
          dimensions: [d.valuePath],
          annotationEnabled,
          filterKey: getFilterKeyForBlock({ block: d, scope: "tags" }),
        });
      }
    }

    return { 
      allBlocks: blocks, 
      vizData: [documentAuthorWidget, ...categorizedBlocks] 
    };
  }, [selectedTpl, onlyPublishingTpls, allTpls, hasExperimentalTopicModelling]);

  // State for widget-specific UI state (widgetComp switches, taxonomyRole, breakdownDisplayType)
  const [widgetUIState, setWidgetUIState] = useState({});

  // Merge base vizData with UI state
  const vizData = useMemo(() => 
    baseVizData.map(widget => ({
      ...widget,
      ...widgetUIState[widget.id]
    })),
    [baseVizData, widgetUIState]
  );

  const ref_filterModuleWrapper = useRef(null);
  const [filterModuleWrapperHeight, setFilterModuleWrapperHeight] = useState("auto");

  // Add resize handling (#17)
  useEffect(() => {
    const updateHeight = () => {
      if (ref_filterModuleWrapper.current) {
        const topOfWrapper = ref_filterModuleWrapper.current.getBoundingClientRect()?.top;
        const toBeHeightOfFilterWrapper = window.innerHeight - topOfWrapper;
        setFilterModuleWrapperHeight(toBeHeightOfFilterWrapper + "px");
      }
    };
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Memoized callbacks (#10)
  const handleWidgetSwitch = useCallback((widgetId, newWidgetType, breakdownDisplayType, taxonomyRole) => {
    setWidgetUIState(prev => ({
      ...prev,
      [widgetId]: {
        ...prev[widgetId],
        widgetComp: newWidgetType,
        breakdownDisplayType: breakdownDisplayType,
        // Always overwrite taxonomyRole — clearing when switching away from
        // annotations is correct, and experimental actions always supply it.
        taxonomyRole,
      }
    }));
  }, []);

  const onVizSegmentClick = useCallback(({ value, activeBlock }) => {
    handleVizSegmentClick({
      clickedSegment: value,
      activeBlockFilterKey: activeBlock.filterKey,
      activeFilters,
      setActiveFilters,
      filterConfig,
      toastActions: { SET_INFO_BANNER, SET_VARIANT },
    });
  }, [activeFilters, filterConfig, SET_INFO_BANNER, SET_VARIANT]);

  // Memoize widget actions (#12)
  const widgetActionsMap = useMemo(() => {
    const map = new Map();
    vizData.forEach(d => {
      const hasBreakdown = d.comp === "GroupQuestionsInputSingle" || d.dimensions.length >= 2;
      const actions = generateWidgetActions({
        currentComp: d.widgetComp,
        hasBreakdown,
        currentBreakdownDisplayType: d.breakdownDisplayType,
        canAnnotate: d.annotationEnabled,
        hasExperimentalTopicModelling,
        isSplitMode,
        taxonomyRole: d.taxonomyRole,
      });

      map.set(d.id, actions.map((action) => ({
        ...action,
        onClick: () => {
          handleWidgetSwitch(d.id, action.value, action?.breakdownDisplayType, action?.taxonomyRole);
        },
      })));
    });
    return map;
  }, [vizData, handleWidgetSwitch, hasExperimentalTopicModelling, isSplitMode]);

  const getWidgetActions = useCallback((d) => {
    return widgetActionsMap.get(d.id) || [];
  }, [widgetActionsMap]);

  const { data: count, status: countStatus } = useGetDataQuery({
    contentTypes: [contentTypeProp],
    useCountDAL: true,
    findQuery: { kp_published_status: 'published' },
    activeFilters: mergedActiveFilters
  });

  const [breakdownDimension, setBreakdownDimension] = useState(undefined);

  const handleSetBreakdown = useCallback((breakdown) => {
    setBreakdownDimension(breakdown);
  }, []);

  const tabOps = useMemo(() => [
    { value: "filters", display: "Filters", message: activeFilters.length ? activeFilters.length : undefined },
    { value: "breakDown", display: "Breakdown", message: breakdownDimension ? 1 : undefined },
  ], [activeFilters.length, breakdownDimension]);

  const [activeTab, setActiveTab] = useState("filters");

  const handleTabChange = useCallback((k, v) => {
    setActiveTab(v);
  }, []);

  const handleTplChange = useCallback((k, v) => {
    setSelectedTpl(v);
  }, []);

  useEffect(() => {
    if (contentTypeProp) {
      setSelectedTpl(contentTypeProp);
    }
  }, [contentTypeProp]);

  useEffect(() => {
    setLoadedWidgetsTracker([]);
    setEmptyWidgetIds(new Set());
    setTrackerResetKey(prev => prev + 1);
  }, [activeFilters, breakdownDimension, selectedTpl, setLoadedWidgetsTracker, setEmptyWidgetIds, setTrackerResetKey]);

  // Memoize the data with breakdown dimensions applied
  const displayVizData = useMemo(() => {
    if (!breakdownDimension) return vizData;
    
    return vizData.map(d => {
      const updatedDimensions = [...d.dimensions, breakdownDimension.value];
      return {
        ...d,
        dimensions: updatedDimensions,
        dimension1Label: getDimensionLabel(updatedDimensions[0], allBlocks),
        dimension2Label: getDimensionLabel(updatedDimensions[1], allBlocks),
        breakdownCategoryName: getDimensionLabel(updatedDimensions[1], allBlocks)
      };
    });
  }, [vizData, breakdownDimension, allBlocks]);

  // Filter out widgets with no data when filters are active
  const filteredDisplayVizData = useMemo(() => {
    if (activeFilters.length === 0 || emptyWidgetIds.size === 0) return displayVizData;
    return displayVizData.filter(widget => !emptyWidgetIds.has(widget.id));
  }, [displayVizData, emptyWidgetIds, activeFilters.length]);

  // Memoize breakdown options
  const breakdownOptions = useMemo(() => 
    getValidBreakdownFields(vizData.map(d => ({
      comp: d.comp,
      value: d.id,
      display: d.display
    }))),
    [vizData]
  );

  // Memoized renderCard callback
  const renderCard = useCallback((cols, colI, d, i) => {
    // Override the shell label for the annotations widget.
    // Experimental: "Annotation Counts: Thematics" / "Annotation Counts: Attributes".
    // Non-experimental: plain "Annotation Counts".
    const annotationLabel =
      d.widgetComp === "WidgetAICategorization"
        ? d.taxonomyRole === "attribute"
          ? "Annotation Counts: Attributes"
          : d.taxonomyRole === "thematic"
          ? "Annotation Counts: Thematics"
          : "Annotation Counts"
        : undefined;
    const widgetLabel =
      annotationLabel ||
      allWidgetsBaseConfs.find(dd => dd.comp === d.widgetComp)?.widgetLabel ||
      undefined;
    return (
      <DataVizWidgetShell
        title={d.label}
        widgetActions={getWidgetActions(d)}
        widgetLabel={widgetLabel}
      >
        <WidgetRenderer
          widget={d}
          index={i}
          activeFilters={mergedActiveFilters}
          breakdownDimension={breakdownDimension}
          onVizSegmentClick={onVizSegmentClick}
          loadedWidgetsTracker={loadedWidgetsTracker}
          taxonomyRole={d.taxonomyRole}
        />
      </DataVizWidgetShell>
    );
  }, [getWidgetActions, mergedActiveFilters, breakdownDimension, onVizSegmentClick, loadedWidgetsTracker]);

  return (
    <div ref={parentWrapperRef}>
      <SidebarLayoutV2
        gridHeightCSS={gridHeightCSS}
        lsbComp={
          <div>
            {!contentTypeProp && (
              <SidebarSection>
                <DropdownSingle
                  S
                  id="tpl"
                  label={"Content Type"}
                  placeholder={"Select One"}
                  saveValueAsString
                  value={selectedTpl}
                  onChange={handleTplChange}
                  options={tplOptions}
                />
              </SidebarSection>
            )}
            <TabBarWrapper>
              <TabBarStyle2
                options={tabOps}
                value={activeTab}
                onChange={handleTabChange}
                saveValueAsString
              />
            </TabBarWrapper>
            <FilterModuleWrapper
              $height={filterModuleWrapperHeight}
              ref={ref_filterModuleWrapper}
            >
              {activeTab === "filters" ? (
                <FilterModule
                  contentTypes={selectedTpl ? [selectedTpl] : []}
                  filterConfig={filterConfig}
                  activeFilters={activeFilters}
                  setActiveFilters={setActiveFilters}
                  usedIn={"overviewDashboard"}
                />
              ) : (
                <FullHeightDiv>
                  <BreakdownModule
                    value={breakdownDimension}
                    onChange={(k, v) => handleSetBreakdown(v)}
                    options={breakdownOptions}
                  />
                </FullHeightDiv>
              )}
            </FilterModuleWrapper>
          </div>
        }
      >
        <MainContentWrapper>
          <PaddingTopBottom20>
            <TotalCountWrapper>
              <TotalCount
                totalCount={countStatus === 'success' ? count?.data?.[0]?.count : countStatus}
                customText={'Total Responses: $count'}
              />
            </TotalCountWrapper>
            <MasonryCardGridShell
              data={filteredDisplayVizData}
              colSetting={{
                xl: 2,
                xl_xxl: 3,
                xxl: 3,
              }}
              renderCard={renderCard}
            />
          </PaddingTopBottom20>
        </MainContentWrapper>
      </SidebarLayoutV2>
    </div>
  );
};

export default OverviewDashboard;
